import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowLeft, CalendarClock, Check, Columns3, Lock, Minus, Pencil, X } from 'lucide-react';
import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api, type Attachment, type UserRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { AttachmentComposer, AttachmentList, AttachmentsCard, DropZone, useUploads } from '../components/Attachments';
import { TaskChecklistCard } from '../components/TaskChecklist';
import { isDepartmentTask, useTaskHolderName } from '../components/TaskHolder';
import { TaskReviewBanner, TaskWorkActions } from '../components/TaskReview';
import { TaskReturnAction, TaskReturnBanner } from '../components/TaskReturn';
import {
  PROGRESS_LABEL,
  RUN_LABEL,
  runTone,
  type LevelProgress,
  type WorkflowJourney,
} from '../components/WorkflowChain';
import { clientName } from './ClientCrs';
import {
  HandedOnWorkCard,
  LeaderTaskActions,
  LeaderTaskBanner,
  LeaderTaskFilesCard,
  LeaderTaskSourceRow,
  leaderActivityLabel,
  taskPath,
} from '../components/LeaderTaskPanels';

const ACTIVITY: Record<string, TranslationKey> = {
  'task.created': 'tasks.activity.created',
  'task.updated': 'tasks.activity.updated',
  'task.assigned': 'tasks.activity.assigned',
  'task.reassigned': 'tasks.activity.reassigned',
  'task.unassigned': 'tasks.activity.unassigned',
  'task.commented': 'tasks.activity.commented',
  'task.files_attached': 'tasks.activity.filesAttached',
  'task.attachment_removed': 'tasks.activity.attachmentRemoved',
  'task.workflow_received': 'tasks.activity.workflowReceived',
  'task.workflow_handed_on': 'tasks.activity.workflowHandedOn',
  'task.workflow_finished': 'tasks.activity.workflowFinished',
  'task.workflow_stopped': 'tasks.activity.workflowStopped',
  'task.workflow_reopened': 'tasks.activity.workflowReopened',
  // The levels its workflow had not reached yet, changed (2026-10-15).
  'task.workflow_edited': 'tasks.activity.workflowEditedPlain',
  'task.deadline_set': 'tasks.activity.deadlineSet',
  'task.deadline_changed': 'tasks.activity.deadlineChanged',
  'task.checklist_added': 'tasks.activity.checklistAdded',
  'task.checklist_done': 'tasks.activity.checklistDone',
  'task.checklist_undone': 'tasks.activity.checklistUndone',
  'task.checklist_renamed': 'tasks.activity.checklistRenamed',
  'task.checklist_removed': 'tasks.activity.checklistRemoved',
  // Whoever opened it, the first time (2026-10-11).
  'task.viewed': 'tasks.activity.viewed',
  // A step sent back to someone earlier to check something, and back again (2026-10-15).
  'task.returned': 'tasks.activity.returnedPlain',
  'task.returned_back': 'tasks.activity.returnedBackPlain',
};

/** History lines that carry words of their own beneath the label: a reason, or a to-do line. */
const WITH_MESSAGE = new Set([
  'task.reassigned',
  'task.unassigned',
  'task.workflow_assigned',
  'task.returned',
  'task.returned_back',
  'task.checklist_added',
  'task.checklist_done',
  'task.checklist_undone',
  'task.checklist_renamed',
  'task.checklist_removed',
]);

/** An edit's history line names what changed, the way the edit form labels it. */
const EDITED_FIELD: Record<string, TranslationKey> = {
  title: 'common.name',
  description: 'common.description',
  projectId: 'common.project',
  departmentId: 'common.department',
  priority: 'common.priority',
  startAt: 'tasks.startDate',
  dueAt: 'tasks.deadline',
  tags: 'tasks.tags',
};
import { formatDateTime, formatNumber, relativeTime } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  ErrorState,
  LoadingBlock,
  PageHeader,
  Textarea,
  priorityTone,
  statusTone,
  useToast,
} from '../components/ui';

/**
 * Route: `/tasks/:id` — one task, where an assignment email and a task
 * notification land: what it is, whose it is, the conversation on it and what
 * has happened to it. Moving it between columns stays on the board.
 */
export function TaskDetailPage() {
  const { id = '' } = useParams();
  const { pathname } = useLocation();
  const { t, locale, localized } = useI18n();
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');
  const uploads = useUploads();
  const holderName = useTaskHolderName();

  const { data: task, isLoading, error, refetch } = useQuery({
    queryKey: ['task', id],
    queryFn: () => api.get<any>(`/tasks/${id}`),
  });

  // A comment is words, files or a voice note — any of them on its own will do.
  const canPost = (comment.trim() !== '' || uploads.fileIds.length > 0) && !uploads.busy;
  const post = useMutation({
    mutationFn: () =>
      api.post(`/tasks/${id}/comments`, {
        body: comment.trim() || undefined,
        fileIds: uploads.fileIds.length ? uploads.fileIds : undefined,
      }),
    onSuccess: () => {
      setComment('');
      uploads.clear();
      toast.success(t('tasks.commentPosted'));
      void queryClient.invalidateQueries({ queryKey: ['task', id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !task) return <ErrorState error={error} onRetry={() => void refetch()} />;
  // A Leaders Task has its own address, under Leaders Tasks — and an ordinary task its own.
  const home = taskPath(task);
  if (!pathname.startsWith(home)) return <Navigate to={home} replace />;
  const leaders = !!task.isLeaderTask;

  const open = !['DONE', 'CANCELLED'].includes(task.status);
  const overdue = task.dueAt && new Date(task.dueAt) < new Date() && open;
  const journey: WorkflowJourney | null = task.workflow;
  // The server's rules for this person: edit until started; team leaders and admins assign and reassign.
  const may = task.can ?? {};
  const mayDate = open && !!may.setDeadline;
  // A finished task is closed (2026-10-19): it takes no comment and no file, in or out.
  const closed = may.comment === false;
  // A workflow hands a step on without a deadline, and its person is asked for one — still theirs while
  // someone earlier checks it (2026-10-15) — until they start on it (2026-10-25: `can.setDeadline` then
  // false, as for everyone). An ordinary task without one keeps only the quiet "Set".
  const askForDeadline =
    !!journey && mayDate && !task.dueAt && (task.returnToUserId ?? task.assigneeUserId) === user?.id;
  const handedOn = !!journey && task.workflowLevel > 1;
  const level = (n: number) => formatNumber(n, locale);
  // A history line: a move between columns, what an edit changed, who it went to — or its own label.
  const activityLabel = (a: any): string => {
    const leaderLine = leaderActivityLabel(a, t, localized);
    if (leaderLine) return leaderLine;
    if (a.toStatus && a.fromStatus) {
      // The arrow points the way the line reads: → in English, ← in Arabic.
      return `${enumLabel(a.fromStatus, locale)} ${locale === 'ar' ? '←' : '→'} ${enumLabel(a.toStatus, locale)}`;
    }
    const fields: string[] = a.action === 'task.updated' && Array.isArray(a.metadata?.fields) ? a.metadata.fields : [];
    if (fields.length > 0) {
      const names = fields.map((f) => (EDITED_FIELD[f] ? t(EDITED_FIELD[f]) : f));
      return t('tasks.activity.edited', { fields: names.join(locale === 'ar' ? '، ' : ', ') });
    }
    // Given to a whole department, which shares it (2026-10-10).
    const department = a.metadata?.department;
    if (department && a.action === 'task.assigned') {
      return t('tasks.activity.assignedToDepartment', { department: localized(department) });
    }
    if (department && a.action === 'task.reassigned') {
      return t('tasks.activity.reassignedToDepartment', { department: localized(department) });
    }
    // Older entries name nobody; they keep the plain label.
    const to = a.metadata?.assignee;
    if (to && a.action === 'task.assigned') return t('tasks.activity.assignedTo', { name: localized(to) });
    if (to && a.action === 'task.reassigned') return t('tasks.activity.reassignedTo', { name: localized(to) });
    if (a.action === 'task.workflow_assigned') {
      // A saved workflow's name, before workflows were built on the task (2026-10-15).
      return a.metadata?.workflow?.name
        ? t('tasks.activity.workflowAssigned', { workflow: localized(a.metadata.workflow) })
        : t('tasks.activity.workflowSent');
    }
    // Sent back to someone earlier to check something, and back again (2026-10-15).
    if (a.action === 'task.returned' && a.metadata?.to) {
      return t('tasks.activity.returned', { name: localized(a.metadata.to) });
    }
    if (a.action === 'task.returned_back' && a.metadata?.to) {
      return t(a.metadata.calledBack ? 'tasks.activity.calledBack' : 'tasks.activity.returnedBack', {
        name: localized(a.metadata.to),
        from: a.metadata.from ? localized(a.metadata.from) : '—',
      });
    }
    // Its workflow's levels ahead, changed (2026-10-15): the ones after this level.
    if (a.action === 'task.workflow_edited' && a.metadata?.level) {
      return t('tasks.activity.workflowEdited', { n: level(a.metadata.level) });
    }
    return ACTIVITY[a.action] ? t(ACTIVITY[a.action]) : enumLabel(a.action.replace('task.', '').toUpperCase(), locale);
  };
  // What a change to its workflow did: who joined it, who left it, how many levels it has now.
  const workflowChange = (m: any): string => {
    const list = (people: any[]) => people.map((p) => localized(p)).join(locale === 'ar' ? '، ' : ', ');
    const parts = [
      ...(m?.added?.length ? [t('tasks.activity.workflowEditedAdded', { names: list(m.added) })] : []),
      ...(m?.removed?.length ? [t('tasks.activity.workflowEditedRemoved', { names: list(m.removed) })] : []),
      ...(m && m.from !== m.to ? [t('tasks.activity.workflowEditedLevels', { from: level(m.from), to: level(m.to) })] : []),
    ];
    return parts.length ? parts.join(' · ') : t('tasks.activity.workflowEditedMoved');
  };
  // What the other steps of the work have attached, step by step.
  const workflowFiles = (journey?.levels ?? []).flatMap((l) =>
    l.tasks.filter((step) => step.attachments.length > 0).map((step) => ({ level: l.level, step })),
  );

  return (
    <>
      <div className="mb-4">
        <Link
          to={leaders ? '/leader-tasks' : '/tasks'}
          className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]"
        >
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {leaders ? t('nav.leaderTasks') : t('nav.tasks')}
        </Link>
      </div>

      <PageHeader
        title={task.title}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            {leaders && <Badge tone="plasma">{t('leaderTasks.badge')}</Badge>}
            <Badge tone={statusTone(task.status)}>{enumLabel(task.status, locale)}</Badge>
            <Badge tone={priorityTone(task.priority)}>{enumLabel(task.priority, locale)}</Badge>
            {overdue && <Badge tone="error">{t('tickets.overdue')}</Badge>}
          </span>
        }
        action={
          leaders ? (
            <LeaderTaskActions task={task} />
          ) : (
            <>
              {/* Whoever has it starts and finishes it; its reviewer marks it done (2026-10-11). */}
              <TaskWorkActions task={task} />
              {/* A step's own person may send it back to someone earlier to check something (2026-10-15). */}
              <TaskReturnAction task={task} />
              {may.edit && (
                <Button icon={<Pencil className="h-4 w-4" />} onClick={() => navigate(`/tasks/${id}/edit`)}>
                  {t('common.edit')}
                </Button>
              )}
              {(may.assign || may.reassign) && (
                <Button onClick={() => navigate(`/tasks/${id}/${may.reassign ? 'reassign' : 'assign'}`)}>
                  {may.reassign ? t('tasks.reassign') : t('tasks.assign')}
                </Button>
              )}
              <Button icon={<Columns3 className="h-4 w-4" />} onClick={() => navigate('/kanban')}>
                {t('kanban.title')}
              </Button>
            </>
          )
        }
      />

      {leaders ? <LeaderTaskBanner task={task} /> : <TaskReviewBanner task={task} />}
      {!leaders && <TaskReturnBanner task={task} />}

      {askForDeadline && (
        <div className="mb-5 flex flex-wrap items-center gap-4 rounded-lg border border-warning/35 bg-warning/8 p-4">
          <CalendarClock className="h-6 w-6 shrink-0 text-warning-fg dark:text-warning-dark" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-title text-[var(--text-primary)]">
              {handedOn ? t('tasks.yourTurn') : t('tasks.setDeadline')}
            </p>
            <p className="mt-0.5 text-body-sm text-muted">
              {handedOn
                ? t('tasks.yourTurnBody', {
                    n: level(task.workflowLevel - 1),
                    workflow: journey.name ? localized({ name: journey.name, nameAr: journey.nameAr }) : t('tasks.theWorkflow'),
                    when: formatDateTime(task.startAt, locale),
                  })
                : t('tasks.noDeadlineBody')}
            </p>
          </div>
          <Button variant="primary" onClick={() => navigate(`${home}/deadline`)}>
            {t('tasks.setDeadline')}
          </Button>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card>
            <CardHeader title={t('common.description')} />
            {/* In its own language's direction, as a comment is: an English full stop stays at its end in Arabic. */}
            <p className="whitespace-pre-wrap text-body text-[var(--text-primary)]" dir={task.description ? 'auto' : undefined}>
              {task.description || <span className="text-muted">{t('tasks.noDescription')}</span>}
            </p>
          </Card>

          <TaskChecklistCard task={task} />

          <AttachmentsCard
            attachments={task.attachments}
            basePath={`/tasks/${id}`}
            queryKey={['task', id]}
            canRemove={(a: Attachment) => !closed && (a.file.uploadedBy?.id === user?.id || can(P.TASKS_MANAGE_ALL))}
            canAdd={!closed}
          />

          {/* A task converted from a client's change request shows what the client sent with it. */}
          {task.sourceChangeRequest && task.changeRequestFiles?.length > 0 && (
            <Card>
              <CardHeader
                title={t('tasks.changeRequestFiles')}
                subtitle={t('tasks.changeRequestFilesHint', {
                  client: clientName(task.sourceChangeRequest.client, locale),
                  number: task.sourceChangeRequest.number,
                })}
              />
              <AttachmentList attachments={task.changeRequestFiles} />
            </Card>
          )}

          <LeaderTaskFilesCard task={task} />

          {workflowFiles.length > 0 && (
            <Card>
              <CardHeader title={t('workflows.filesTitle')} subtitle={t('workflows.filesHint')} />
              <div className="space-y-4">
                {workflowFiles.map(({ level: n, step }) => (
                  <div key={step.id}>
                    <Link
                      to={`/tasks/${step.id}`}
                      className="mb-1.5 inline-block text-caption font-medium text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {t('workflows.stepBy', { n: level(n), name: localized(step.assignee) })}
                    </Link>
                    <AttachmentList attachments={step.attachments} showUploader />
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card>
            <CardHeader title={t('tasks.comments')} />
            {task.comments.length === 0 ? (
              <p className="mb-4 text-body-sm text-muted">{t('tasks.noComments')}</p>
            ) : (
              <ul className="mb-4 space-y-3">
                {task.comments.map((c: any) => (
                  <li key={c.id} className="rounded-md bg-[var(--surface-sunken)] px-3 py-2.5">
                    <p className="text-caption text-muted">
                      <span className="font-medium text-[var(--text-secondary)]">{localized(c.author)}</span> ·{' '}
                      <span title={formatDateTime(c.createdAt, locale)}>{relativeTime(c.createdAt, locale)}</span>
                      {/* Handed on with the work from another step: its writer may not be able to open this task. */}
                      {c.carriedFrom && (
                        <>
                          {' · '}
                          <span className="text-primary-600 dark:text-primary-400" title={t('tasks.carriedCommentHint')}>
                            {c.carriedFrom.level && c.carriedFrom.level < task.workflowLevel
                              ? t('tasks.fromLevel', { n: level(c.carriedFrom.level) })
                              : t('tasks.carriedComment')}
                          </span>
                        </>
                      )}
                    </p>
                    {c.body && (
                      // Each comment reads in its own language's direction, whatever the page's.
                      <p className="mt-1 whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                        {c.body}
                      </p>
                    )}
                    <AttachmentList attachments={c.attachments} className="mt-2" />
                  </li>
                ))}
              </ul>
            )}
            {closed ? (
              <p className="flex items-start gap-2 rounded-md border hairline px-3 py-2.5 text-body-sm text-muted">
                <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                {t(journey ? 'tasks.stepClosed' : 'tasks.taskClosed')}
              </p>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (canPost) post.mutate();
                }}
              >
                <DropZone uploads={uploads}>
                  <Textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onPaste={uploads.onPaste}
                    placeholder={t('tasks.addComment')}
                    aria-label={t('tasks.addComment')}
                  />
                  <AttachmentComposer
                    uploads={uploads}
                    className="mt-2"
                    actions={
                      <Button type="submit" variant="primary" size="sm" loading={post.isPending} disabled={!canPost}>
                        {t('tasks.post')}
                      </Button>
                    }
                  />
                </DropZone>
              </form>
            )}
          </Card>
        </div>

        <div className="space-y-5">
          <Card>
            <CardHeader title={t('tickets.details')} />
            <dl>
              <Detail label={t('common.project')}>
                {task.project ? (
                  <Link to={`/projects/${task.project.id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                    {localized(task.project)}
                  </Link>
                ) : (
                  t('common.general')
                )}
              </Detail>
              <Detail label={leaders ? t('leaderTasks.askedOf') : t('common.assignee')}>
                {holderName(task) ?? t('common.unassigned')}
                {/* Its whole department shares it (2026-10-10): everyone there works on it. */}
                {isDepartmentTask(task) && <span className="block text-caption text-faint">{t('tasks.sharedHint')}</span>}
              </Detail>
              {task.department && <Detail label={t('common.department')}>{localized(task.department)}</Detail>}
              {task.sourceQTask && (
                <Detail label={t('tasks.fromQTask')}>
                  <Link to={`/q-tasks/${task.sourceQTask.id}`} className="font-mono text-primary-600 dark:text-primary-400 hover:underline">
                    {task.sourceQTask.number}
                  </Link>
                </Detail>
              )}
              {task.sourceChangeRequest && (
                <Detail label={t('tasks.fromChangeRequest')}>
                  {can(P.CLIENT_CRS_MANAGE) ? (
                    <Link
                      to={`/client-crs/${task.sourceChangeRequest.id}`}
                      className="font-mono text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      {task.sourceChangeRequest.number}
                    </Link>
                  ) : (
                    <span className="font-mono">{task.sourceChangeRequest.number}</span>
                  )}
                  <span className="block text-caption text-faint">
                    {clientName(task.sourceChangeRequest.client, locale)}
                  </span>
                </Detail>
              )}
              <LeaderTaskSourceRow task={task} />
              {task.sourceAnnouncement && (
                <Detail label={t('tasks.fromAnnouncement')}>
                  {task.sourceAnnouncement.deletedAt ? (
                    <span dir="auto">
                      {task.sourceAnnouncement.title}{' '}
                      <Badge className="ms-1">{t('announcements.deletedBadge')}</Badge>
                    </span>
                  ) : (
                    <Link
                      to={`/announcements/${task.sourceAnnouncement.id}`}
                      className="text-primary-600 dark:text-primary-400 hover:underline"
                      dir="auto"
                    >
                      {task.sourceAnnouncement.title}
                    </Link>
                  )}
                </Detail>
              )}
              {task.startAt && (
                <Detail label={t('tasks.startDate')}>
                  <span className="tabular">{formatDateTime(task.startAt, locale)}</span>
                  {handedOn && (
                    <span className="block text-caption text-faint">
                      {t('tasks.startedWhen', { n: level(task.workflowLevel - 1) })}
                    </span>
                  )}
                </Detail>
              )}
              <Detail label={t('tasks.deadline')}>
                <span className={overdue ? 'text-error-fg dark:text-error-dark tabular' : 'tabular'}>
                  {task.dueAt ? formatDateTime(task.dueAt, locale) : handedOn ? t('tasks.notSet') : '—'}
                </span>
                {mayDate && (
                  <Link
                    to={`${home}/deadline`}
                    className="ms-2 text-caption text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {task.dueAt ? t('tasks.change') : t('tasks.set')}
                  </Link>
                )}
              </Detail>
              <Detail label={leaders ? t('leaderTasks.sentBy') : t('tasks.createdBy')}>{localized(task.createdBy)}</Detail>
              <Detail label={t('common.createdAt')}>
                <span className="tabular">{formatDateTime(task.createdAt, locale)}</span>
              </Detail>
              {task.tags?.length > 0 && (
                <Detail label={t('tasks.tags')}>
                  <span className="flex flex-wrap justify-end gap-1">
                    {task.tags.map((tag: string) => (
                      <Badge key={tag}>{tag}</Badge>
                    ))}
                  </span>
                </Detail>
              )}
            </dl>
          </Card>

          {journey && <WorkflowCard journey={journey} taskId={task.id} canEdit={!!may.editWorkflow} />}
          {leaders && <HandedOnWorkCard task={task} />}

          <Card>
            <CardHeader title={t('tasks.activity')} />
            <ul className="space-y-2">
              {task.activities.map((a: any) => (
                <li key={a.id} className="text-body-sm">
                  <span className="text-[var(--text-primary)]">{activityLabel(a)}</span>
                  {/* Why it changed hands, or which to-do line — in the words, and the direction, it was written in. */}
                  {WITH_MESSAGE.has(a.action) && a.message && (
                    <span className="mt-0.5 block whitespace-pre-wrap break-words text-muted" dir="auto">
                      {a.message}
                    </span>
                  )}
                  {a.action === 'task.workflow_edited' && a.metadata && (
                    <span className="mt-0.5 block break-words text-muted">{workflowChange(a.metadata)}</span>
                  )}
                  {/* A reworded line: the words it had, struck through. */}
                  {a.action === 'task.checklist_renamed' && a.metadata?.from && (
                    <span className="block whitespace-pre-wrap break-words text-faint line-through" dir="auto">
                      {a.metadata.from}
                    </span>
                  )}
                  <span className="block text-caption text-muted">
                    {a.actor ? localized(a.actor) : '—'} · {relativeTime(a.occurredAt, locale)}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </>
  );
}

/**
 * The journey this task is one step of: every level of the workflow, top to
 * bottom, with the tasks it became on each — or, not reached yet, who is waiting
 * for it. Each step opens its task. Whoever may change the levels not reached yet
 * (2026-10-15) has Edit beside its status.
 */
function WorkflowCard({ journey, taskId, canEdit }: { journey: WorkflowJourney; taskId: string; canEdit: boolean }) {
  const { t, locale, localized } = useI18n();
  // The task's own workflow (2026-10-15): who started it — and the saved workflow's name, for work
  // started from one before workflows were built on the task.
  const startedBy = t('tasks.workflowStartedBy', { name: localized(journey.createdBy) });
  return (
    <Card>
      <CardHeader
        title={t('tasks.workflow')}
        subtitle={
          journey.name ? `${localized({ name: journey.name, nameAr: journey.nameAr })} · ${startedBy}` : startedBy
        }
        action={
          <span className="flex items-center gap-3">
            {canEdit && (
              <Link
                to={`/tasks/${taskId}/workflow`}
                className="inline-flex items-center gap-1 text-body-sm text-primary-600 dark:text-primary-400 hover:underline"
                aria-label={t('tasks.workflowEdit.title')}
                title={t('tasks.workflowEdit.title')}
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden />
                {t('common.edit')}
              </Link>
            )}
            <Badge tone={runTone(journey.status)}>{t(RUN_LABEL[journey.status])}</Badge>
          </span>
        }
      />
      <ol className="space-y-4">
        {journey.levels.map((level, index) => (
          <li key={level.level} className="relative flex gap-3">
            {index < journey.levels.length - 1 && (
              <span className="absolute start-3.5 top-8 -bottom-4 w-px bg-[var(--line)]" aria-hidden />
            )}
            <StepMarker progress={level.progress} n={formatNumber(level.level, locale)} />
            <div className="min-w-0 flex-1 pt-0.5">
              <p className="text-body-sm font-medium text-[var(--text-primary)]">
                {t('workflows.levelN', { n: formatNumber(level.level, locale) })}
                <span className="ms-1.5 text-caption font-normal text-muted">· {t(PROGRESS_LABEL[level.progress])}</span>
              </p>
              {level.tasks.length > 0 || (level.others ?? []).length > 0 ? (
                <ul className="mt-1 space-y-1">
                  {level.tasks.map((step) => (
                    // The column is narrow: the status drops under the name rather than squeeze it.
                    <li
                      key={step.id}
                      className={clsx(
                        'flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 text-body-sm',
                        step.id === taskId && '-mx-1.5 rounded-sm bg-primary/8 px-1.5 py-0.5',
                      )}
                    >
                      {/* A step out for a check is its own person's, with whoever is checking it (2026-10-15). */}
                      {step.id === taskId ? (
                        <span className="min-w-0 break-words font-medium text-[var(--text-primary)]">
                          {localized(step.returnTo ?? step.assignee)}
                          <span className="ms-1 text-caption font-normal text-primary-600 dark:text-primary-400">· {t('workflows.thisTask')}</span>
                          {step.returnTo && <CheckingBy person={step.assignee} />}
                        </span>
                      ) : (
                        <span className="min-w-0 break-words">
                          <Link to={`/tasks/${step.id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                            {localized(step.returnTo ?? step.assignee)}
                          </Link>
                          {step.returnTo && <CheckingBy person={step.assignee} />}
                        </span>
                      )}
                      <Badge tone={statusTone(step.status)}>{enumLabel(step.status, locale)}</Badge>
                    </li>
                  ))}
                  {/* Someone else's step: whose it is, and no more — each step is its own person's. */}
                  {(level.others ?? []).map((person, i) => (
                    <li
                      key={`${person.id}-${i}`}
                      className="flex items-center gap-1.5 text-body-sm text-muted"
                      title={t('workflows.privateStep')}
                    >
                      <Lock className="h-3.5 w-3.5 shrink-0 text-faint" aria-hidden />
                      <span className="min-w-0 break-words">{localized(person)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                level.people.length > 0 && (
                  <p className="mt-0.5 text-caption text-muted">
                    {level.people.map((p) => localized(p)).join(locale === 'ar' ? '، ' : ', ')}
                  </p>
                )
              )}
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}

/** "with Ali to check", beside a step out for a check. */
function CheckingBy({ person }: { person: UserRef | null }) {
  const { t, localized } = useI18n();
  return (
    <span className="block text-caption font-normal text-warning-fg dark:text-warning-dark">
      {t('tasks.return.stepChecking', { name: localized(person) })}
    </span>
  );
}

function StepMarker({ progress, n }: { progress: LevelProgress; n: string }) {
  return (
    <span
      className={clsx(
        'relative grid h-7 w-7 shrink-0 place-items-center rounded-full text-caption font-semibold tabular',
        progress === 'done' && 'bg-success/15 text-success-fg dark:text-success',
        progress === 'current' && 'bg-primary text-white',
        progress === 'stopped' && 'bg-warning/15 text-warning-fg dark:text-warning-dark',
        (progress === 'waiting' || progress === 'skipped' || progress === 'unreached') &&
          'bg-[var(--surface-sunken)] text-[var(--text-tertiary)]',
      )}
      aria-hidden
    >
      {progress === 'done' ? (
        <Check className="h-4 w-4" />
      ) : progress === 'skipped' ? (
        <Minus className="h-4 w-4" />
      ) : progress === 'stopped' ? (
        <X className="h-4 w-4" />
      ) : (
        n
      )}
    </span>
  );
}
